import { useCallback } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styled from "@emotion/styled";
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import { differenceInMonths } from "date-fns";
import { useRangeSelectControl, useChartColors } from "@hooks";
import { RangeSelector } from "@rentablo/components";
import { useChartOptions } from "./useChartOptions";

const intervalTypes: IntervalType[] = [
  "oneMonth",
  "oneYear",
  "threeYears",
  "fiveYears",
  "sinceBeginning",
];

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export interface SecuritiesDateAndPriceChartDataItem {
  date: Date;
  value: number;
}

export interface SecuritiesDateAndPriceChartProps {
  data: SecuritiesDateAndPriceChartDataItem[];
  onDateSelect: (type: IntervalType, value: RangeType) => void;
  onValuesSelect: (value?: number, date?: Date) => void;
}

const getTickFormat = (start?: Date, end?: Date) => {
  if (!start || !end) return "MM''yy";

  const monthDiff = differenceInMonths(end, start);

  if (monthDiff < 10) {
    return "dd.MM''yy";
  }

  return "MM''yy";
};

export const SecuritiesDateAndPriceChart = (
  props: SecuritiesDateAndPriceChartProps
) => {
  const { data = [], onDateSelect, onValuesSelect } = props;
  const chartColors = useChartColors("securitiesChart");

  const theme = useTheme();
  const { range, intervalType, customIntervalSelect, selectInterval } =
    useRangeSelectControl({
      onSelect: onDateSelect,
      firstDate: data[0] ? data[0].date : new Date(),
    });
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const tickFormat = getTickFormat(range.startDate, range.endDate);

  // API returns daily-cadence data for all supported ranges, so data.length
  // doubles as a day count — useChartOptions converts this to ms-per-tick.
  const tickInterval = Math.floor(data.length / (isXs ? 6 : 12));

  const handlePointClick = useCallback(
    (value: number, date: Date) => {
      onValuesSelect(value, date);
    },
    [onValuesSelect]
  );

  const chartOptions = useChartOptions({
    data,
    theme,
    chartColors,
    tickFormat,
    tickInterval,
    onPointClick: handlePointClick,
  });

  return (
    <Wrapper>
      <RangeSelector
        intervalType={intervalType}
        intervalTypes={intervalTypes}
        onSelect={selectInterval}
        onCustomSelect={customIntervalSelect}
      />

      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Wrapper>
  );
};
